import {
  PurchaseOrderProductLineItem,
  UpsertPurchaseOrderProductInventoryUnit,
  UpsertPurchaseOrderProductInventoryUnits,
  UpsertPurchaseOrderProductStock,
} from '../../../clients/quipli-service/purchase-order/purchase-order';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { useCallback, useMemo, useState } from 'react';
import { PurchaseOrderStep } from './use-receive-purchase-order-workflow';

// Initialize stock update
function initStockUpdate(pop: PurchaseOrderProductLineItem): UpsertPurchaseOrderProductStock {
  if (pop.product.allowSale && !pop.product.allowRent) {
    return {
      purchaseOrderProductId: pop.id,
      saleQuantity: pop.quantity,
      rentalQuantity: 0,
    };
  }
  if (pop.product.allowRent && !pop.product.allowSale) {
    return {
      purchaseOrderProductId: pop.id,
      saleQuantity: 0,
      rentalQuantity: pop.quantity,
    };
  }

  // If product is for sale and rent, let admin decide quantities
  return {
    purchaseOrderProductId: pop.id,
    saleQuantity: 0,
    rentalQuantity: 0,
  };
}

// Initialize unit update
function initUnitUpdate(pop: PurchaseOrderProductLineItem): UpsertPurchaseOrderProductInventoryUnits {
  const units = [];
  for (let i = 0; i < pop.quantity; i++) {
    units.push({
      unitNumber: '',
      serialNumber: '',
      forRent: pop.product.allowRent && !pop.product.allowSale,
      forSale: pop.product.allowSale && !pop.product.allowRent,
    });
  }
  return {
    purchaseOrderProductId: pop.id,
    units,
  };
}

// Validate stock
function validateStock(stock: UpsertPurchaseOrderProductStock, expectedQuantity: number): string[] {
  const errors = [];
  if (expectedQuantity !== stock.rentalQuantity + stock.saleQuantity) {
    errors.push('Sale Quantity + Rental Quantity do not equal order quantity');
  }
  return errors;
}

// Validate unit
function validateUnit(u: UpsertPurchaseOrderProductInventoryUnit): string[] {
  const errors = [];
  if (!u.unitNumber) {
    errors.push('Unit number is required');
  }
  if (!u.serialNumber) {
    errors.push('Serial number is required');
  }
  if (!u.forSale && !u.forRent) {
    errors.push('Unit must be listed as for sale or rent');
  }
  return errors;
}

export const useReceivePurchaseOrderInventory = (
  receivableProducts: PurchaseOrderProductLineItem[],
  currentStep: PurchaseOrderStep,
) => {
  // Data management
  const [stockUpdates, setStockUpdates] = useState<UpsertPurchaseOrderProductStock[]>(
    receivableProducts.filter((pop) => pop.product.trackingType === ProductTrackingType.Quantity).map(initStockUpdate),
  );
  const [unitUpdates, setUnitUpdates] = useState<UpsertPurchaseOrderProductInventoryUnits[]>(
    receivableProducts
      .filter((pop) => pop.product.trackingType === ProductTrackingType.SerialUnits)
      .map(initUnitUpdate),
  );

  // Update stock
  const updateStock = useCallback((update: UpsertPurchaseOrderProductStock) => {
    setStockUpdates((sus) =>
      sus.map((sus) => {
        if (sus.purchaseOrderProductId === update.purchaseOrderProductId) {
          const product = receivableProducts.find((pop) => pop.id === update.purchaseOrderProductId)!;

          // If product is not for rent, don't allow rental quantity
          if (!product.product.allowRent) {
            update.rentalQuantity = 0;
          }
          // If product is not for sale, don't allow sale quantity
          if (!product.product.allowSale) {
            update.saleQuantity = 0;
          }
          return update;
        } else {
          return sus;
        }
      }),
    );
  }, []);

  // Update units
  const updateUnits = useCallback((productId: string, units: UpsertPurchaseOrderProductInventoryUnit[]) => {
    setUnitUpdates((sus) =>
      sus.map((sus) => {
        if (sus.purchaseOrderProductId === productId) {
          return { purchaseOrderProductId: productId, units };
        } else {
          return sus;
        }
      }),
    );
  }, []);

  // Get current data based on step type
  // Current stock
  const currentStock =
    currentStep.type === 'quantity'
      ? stockUpdates.find((s: UpsertPurchaseOrderProductStock) => s.purchaseOrderProductId === currentStep.product.id)
      : undefined;

  // Current unit update
  const currentUnitUpdate =
    currentStep.type === 'unit'
      ? unitUpdates.find(
          (u: UpsertPurchaseOrderProductInventoryUnits) => u.purchaseOrderProductId === currentStep.product.id,
        )
      : undefined;

  // Validation logic
  const currentStepErrors = useMemo(() => {
    if (currentStep.type === 'quantity' && currentStock) {
      const totalQuantity = currentStock.saleQuantity + currentStock.rentalQuantity;
      return totalQuantity !== currentStep.product.quantity
        ? { quantity: 'Sale Quantity + Rental Quantity must equal order quantity' }
        : {};
    }
    if (currentStep.type === 'unit' && currentUnitUpdate && currentStep.unitIndex !== undefined) {
      const currentUnit = currentUnitUpdate.units[currentStep.unitIndex];
      const errors: Record<string, string> = {};
      if (!currentUnit.unitNumber) errors.unitNumber = 'Unit number is required';
      if (!currentUnit.serialNumber) errors.serialNumber = 'Serial number is required';
      if (!currentUnit.forSale && !currentUnit.forRent) errors.saleRent = 'Unit must be listed as for sale or rent';
      return errors;
    }
    return {};
  }, [currentStep, currentStock, currentUnitUpdate]);

  // Check if the current step is valid
  const isCurrentStepValid = useMemo(() => {
    if (currentStep.type === 'quantity' && currentStock) {
      const errors = validateStock(currentStock, currentStep.product.quantity);
      return errors.length === 0;
    }
    if (currentStep.type === 'unit' && currentUnitUpdate && currentStep.unitIndex !== undefined) {
      const currentUnit = currentUnitUpdate.units[currentStep.unitIndex];
      const errors = validateUnit(currentUnit);
      return errors.length === 0;
    }
    return true;
  }, [currentStep, currentStock, currentUnitUpdate]);

  // Check if the purchase order can be received
  const canReceive = useMemo(() => {
    // Create a lookup map for efficient product access
    const productMap = new Map(receivableProducts.map((pop) => [pop.id, pop]));

    const allErrors = [
      ...stockUpdates.map((s) => {
        const product = productMap.get(s.purchaseOrderProductId)!;
        return validateStock(s, product.quantity);
      }),
      ...unitUpdates.flatMap((uus) => uus.units).map((u) => validateUnit(u)),
    ];

    return allErrors.every((errs) => errs.length === 0);
  }, [stockUpdates, unitUpdates, receivableProducts]);

  return {
    // Data
    stockUpdates,
    unitUpdates,
    updateStock,
    updateUnits,
    currentStock,
    currentUnitUpdate,

    // Validation
    currentStepErrors,
    isCurrentStepValid,
    canReceive,
  };
};
